import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, Snackbar } from '@mui/material';
import { getPaymentsList, createPayment } from '../../services/paymentService';
import { getInvoicesList } from '../../services/invoiceService';
import AddPaymentDialog from '../../components/forms/AddPaymentDialog';

function flattenErrors(obj, prefix = '') {
  let messages = [];
  for (const [key, value] of Object.entries(obj)) {
    const label = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      messages.push(`${label}: ${value.join(', ')}`);
    } else if (typeof value === 'object' && value !== null) {
      messages = messages.concat(flattenErrors(value, label));
    } else {
      messages.push(`${label}: ${value}`);
    }
  }
  return messages;
}

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPaymentsList();
      setPayments(data);
    } catch (e) {
      setError('Failed to load payments list.');
    }
    setLoading(false);
  };

  const fetchInvoices = async () => {
    try {
      const data = await getInvoicesList();
      setInvoices(data);
    } catch (e) {}
  };

  useEffect(() => {
    fetchPayments();
    fetchInvoices();
  }, []);

  const handleAddPayment = async (form) => {
    try {
      await createPayment(form);
      setSnackbar({ open: true, message: 'Payment added successfully!', severity: 'success' });
      setAddOpen(false);
      fetchPayments();
    } catch (err) {
      let message = 'Failed to add payment';
      if (err.response && err.response.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          message = data;
        } else if (typeof data === 'object') {
          message = flattenErrors(data).join(' | ');
        }
      }
      setSnackbar({ open: true, message, severity: 'error' });
      throw new Error(message);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>Payments</Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddOpen(true)}>Add Payment</Button>
      <AddPaymentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAddPayment}
        invoices={invoices}
      />
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : payments.length === 0 ? (
          <Typography>No payments data yet.</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Amount Paid</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Payment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.payment_id || payment.id}>
                    <TableCell>{payment.payment_id || payment.id}</TableCell>
                    <TableCell>
                      #{payment.invoice_info?.invoice_id ?? payment.invoice} {/* fallback */}
                    </TableCell>
                    <TableCell>\u00A3{payment.paid_amount}</TableCell>
                    <TableCell>{payment.payment_method}</TableCell>
                    <TableCell>{new Date(payment.payment_datetime).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentsPage; 
